import { api } from '@/api';
import { TransactionType } from '@polybank/shared';
import { AIRuleResponse } from '@polybank/shared/types/ai-contracts';

export interface ExecuteRuleResult {
  success: boolean;
  data?: {
    transactionsUpdated: number;
    processingTimeMs: number;
  };
}

export interface MatchingTransaction {
  id: string;
  description: string;
  category_id?: string;
  bank_account_id: string;
  transaction_type: TransactionType;
  amount: number;
  currency: string;
  transaction_date: string;
  absoluteAmount?: number;
}

export interface MatchingTransactionsResult {
  count: number;
  transactions: MatchingTransaction[];
}

/**
 * Service for rules API operations (execution, deletion, transaction matching)
 */
class RulesApiService {
  /**
   * Execute a rule by ID
   */
  async executeRule(
    ruleId: string,
    forceApply: boolean = false
  ): Promise<ExecuteRuleResult> {
    const result = await api.v1.rules
      .execute({ id: ruleId })
      .post({ forceApply });
    if (result.error) throw result.error;
    return result.data as ExecuteRuleResult;
  }

  /**
   * Delete a rule by ID
   */
  async deleteRule(
    ruleId: string,
    undoActions: boolean = false
  ): Promise<{ success: boolean }> {
    const result = await api.v1.rules?.({ id: ruleId }).delete(undefined, {
      query: { undoActions: undoActions ? 'true' : 'false' },
    });
    if (result?.error) throw result.error;
    return result?.data as { success: boolean };
  }

  /**
   * Find transactions matching a rule's conditions
   */
  async findMatchingTransactions(
    conditions: AIRuleResponse['conditions']
  ): Promise<MatchingTransactionsResult> {
    const result = await api.v1.rules['tx-match'].post(conditions);
    if (result.error) throw result.error;
    return result.data as MatchingTransactionsResult;
  }

  /**
   * Execute a batch of rules via SSE streaming
   */
  async executeBatch(
    ruleIds: string[],
    forceApply: boolean,
    signal?: AbortSignal
  ) {
    return api.v1.rules['execute-batch'].post(
      { ruleIds, forceApply },
      { fetch: { signal } }
    );
  }

  /**
   * Count transactions matching a rule's conditions
   */
  async countMatchingTransactions(
    conditions: AIRuleResponse['conditions']
  ): Promise<MatchingTransactionsResult> {
    const result = await api.v1.rules['tx-match-count'].post(conditions);
    if (result.error) throw result.error;
    return result.data as MatchingTransactionsResult;
  }
}

export const rulesApiService = new RulesApiService();
